import { m } from "@nerves/i18n/messages";
import { X } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import {
  Dialog as AriaDialog,
  Modal as AriaModal,
  DialogTrigger,
  Heading,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components";
import { cx } from "@/lib/cx";
import { IconButton } from "./IconButton";

export { DialogTrigger };

type Placement = "center" | "top" | "left";

export interface ModalProps extends Omit<ModalOverlayProps, "className" | "children"> {
  /** center: standard dialog. top: command palette style. left: navigation drawer. */
  placement?: Placement;
  size?: "sm" | "md" | "lg" | "palette";
  children: ReactNode | ((opts: { close: () => void }) => ReactNode);
}

const sizes = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", palette: "max-w-[640px]" };

/**
 * Modal surface. React Aria traps focus, restores it to the trigger on close, and handles Escape.
 * Compose: <Modal><Dialog aria-label | <DialogTitle>>...</Dialog></Modal>.
 */
export function Modal({ placement = "center", size = "md", children, isDismissable = true, ...props }: ModalProps) {
  return (
    <ModalOverlay
      isDismissable={isDismissable}
      className={cx(
        "fixed inset-0 z-50 flex bg-scrim anim-fade",
        placement === "center" && "items-center justify-center p-4",
        placement === "top" && "items-start justify-center p-4 pt-[14vh]",
        placement === "left" && "items-stretch justify-start",
      )}
      {...props}
    >
      <AriaModal
        className={cx(
          "bg-dialog-bg text-fg shadow-lg outline-none",
          placement === "left"
            ? "anim-drawer h-full w-[min(288px,86vw)] border-r border-popover-border"
            : cx(
                placement === "top" ? "anim-scale" : "anim-pop",
                "w-full overflow-hidden rounded-panel border border-popover-border",
                sizes[size],
              ),
        )}
      >
        {typeof children === "function" ? (
          <AriaDialog className="outline-none">
            {({ close }) => (children as (o: { close: () => void }) => ReactNode)({ close })}
          </AriaDialog>
        ) : (
          children
        )}
      </AriaModal>
    </ModalOverlay>
  );
}

export function Dialog({ className, ...props }: React.ComponentProps<typeof AriaDialog>) {
  return <AriaDialog className={cx("flex max-h-[85dvh] flex-col outline-none", className as string)} {...props} />;
}

export function DialogHeader({ title, onClose }: { title: ReactNode; onClose?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
      <Heading slot="title" className="min-w-0 text-base font-semibold tracking-snug text-fg">
        {title}
      </Heading>
      {onClose ? <IconButton aria-label={m.common_close()} icon={X} size="sm" onPress={onClose} /> : null}
    </div>
  );
}

export function DialogBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("min-h-0 overflow-auto px-5 py-2 text-sm text-fg-muted", className)}>{children}</div>;
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center justify-end gap-2 px-5 pt-3 pb-4">{children}</div>;
}
